import React, { useState, useCallback } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import Pill from "../../common/Pill/Pill";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import { CourseGenerationStatus } from "../../../graphql/generated/graphql";

interface Course {
  id: string;
  name?: string | null;
  course_code?: string | null;
  description?: string | null;
  startDate?: string | null;
  isDraft?: boolean | null;
  generationStatus?: CourseGenerationStatus | null;
  last_modified?: string | null;
  enrolledStudents?: Array<{
    id: string;
    name: string;
  }> | null;
  modules: Array<{
    id: string;
    title?: string;
    aiTutors: Array<{
      id: string;
      title: string;
    }>;
    assignments: Array<{
      id: string;
      name?: string;
    }>;
    axioResources: Array<{
      id: string;
      name?: string;
    }>;
    reviews?: Array<{
      id: string;
    }>;
  }>;
}

interface TeachingCardProps {
  course: Course;
  onCardPress: (courseId: string) => void;
}

const TeachingCard: React.FC<TeachingCardProps> = ({ course, onCardPress }) => {
  const [isHovered, setIsHovered] = useState(false);

  const formatCourseNameWithCode = (courseName: string, courseCode?: string | null): string => {
    if (courseCode && courseCode.trim()) {
      return `${courseCode} ${courseName}`;
    }
    return courseName;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "No start date";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    });
  };

  const formatLastUpdated = (dateString: string | null) => {
    if (!dateString) return "Never updated";
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Updated today";
    if (diffDays === 1) return "Updated 1 day ago";
    if (diffDays < 7) return `Updated ${diffDays} days ago`;
    if (diffDays < 14) return "Updated 1 week ago";
    if (diffDays < 30) return `Updated ${Math.floor(diffDays / 7)} weeks ago`;
    return `Updated ${Math.floor(diffDays / 30)} months ago`;
  };

  const getUpdateDotColor = (dateString: string | null) => {
    if (!dateString) return "#64748B"; // Gray for never updated

    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 2) {
      return "#F59E0B"; // Amber/Orange for last 2 days
    } else if (diffDays <= 7) {
      return "#10B981"; // Green for 3 days to 1 week
    } else {
      return "#64748B"; // Gray for over 1 week
    }
  };

  const getCourseStatusPill = (course: Course) => {
    if (course.isDraft) {
      return { text: "DRAFT", backgroundColor: "#FEF3C7", textColor: "#92400E" };
    }
    return { text: "PUBLISHED", backgroundColor: "#D1FAE5", textColor: "#065F46" };
  };

  const getActivityCount = (modules: Course["modules"]) => {
    return modules.reduce((total, module) => {
      return (
        total +
        module.aiTutors.length +
        module.assignments.length +
        module.axioResources.length +
        (module.reviews?.length || 0)
      );
    }, 0);
  };

  const handleCardPress = useCallback(() => {
    onCardPress(course.id);
  }, [course.id, onCardPress]);

  const statusPill = getCourseStatusPill(course);
  const studentCount = course.enrolledStudents?.length || 0;
  const moduleCount = course.modules.length;
  const activityCount = getActivityCount(course.modules);
  const displayName = formatCourseNameWithCode(course.name, course.course_code);

  return (
    <>
      <Pressable
        testID={`teaching-course-card-${course.id}`}
        style={[styles.card, isHovered && styles.cardHovered]}
        onPress={handleCardPress}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}>
        <View style={styles.cardHeader}>
          <View style={styles.pillContainer}>
            <Pill
              text={statusPill.text}
              backgroundColor={statusPill.backgroundColor}
              textColor={statusPill.textColor}
            />
          </View>
        </View>

        <CustomSpacing type="vertical" size="l" />
        <View style={styles.cardContent}>
          <CustomText
            text={displayName}
            size="l"
            weight="ultraBold"
            useTranslationText={false}
            style={styles.courseName}
            numberOfLines={2}
            ellipsizeMode="tail"
          />

          <CustomSpacing type="vertical" size="s" />

          <CustomText
            text={`${course.course_code} • ${formatDate(course.startDate)}`}
            weight="medium"
            useTranslationText={false}
            style={styles.courseCode}
          />

          <CustomSpacing type="vertical" size="s" />

          <CustomText
            text={course.description || "No description available"}
            size="s"
            weight="medium"
            useTranslationText={false}
            style={styles.description}
            numberOfLines={3}
            ellipsizeMode="tail"
          />
        </View>

        <CustomSpacing type="vertical" size="xl" />

        <View style={styles.statsContainer}>
          <View style={styles.statItem}>
            <CustomText
              text={studentCount.toString()}
              size="xl"
              weight="ultraBold"
              useTranslationText={false}
              style={styles.statNumber}
            />
            <CustomText text="STUDENTS" size="xs" weight="medium" useTranslationText={false} style={styles.statLabel} />
          </View>

          <View style={styles.statItem}>
            <CustomText
              text={moduleCount.toString()}
              size="xl"
              weight="ultraBold"
              useTranslationText={false}
              style={styles.statNumber}
            />
            <CustomText text="MODULES" size="xs" weight="medium" useTranslationText={false} style={styles.statLabel} />
          </View>

          <View style={styles.statItem}>
            <CustomText
              text={activityCount.toString()}
              size="xl"
              weight="ultraBold"
              useTranslationText={false}
              style={styles.statNumber}
            />
            <CustomText
              text="ACTIVITIES"
              size="xs"
              weight="medium"
              useTranslationText={false}
              style={styles.statLabel}
            />
          </View>
        </View>

        <CustomSpacing type="vertical" size="l" />

        <View style={styles.footer}>
          <View style={styles.updateIndicator}>
            <View style={[styles.updateDot, { backgroundColor: getUpdateDotColor(course.last_modified) }]} />
            <CustomText
              text={formatLastUpdated(course.last_modified)}
              weight="medium"
              useTranslationText={false}
              style={styles.updateText}
            />
          </View>
        </View>
      </Pressable>
    </>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
    minHeight: 340,
  },
  cardHovered: {
    borderColor: "#3B82F6",
    shadowColor: "#3B82F6",
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingHorizontal: 20,
    paddingTop: 20,
  },
  pillContainer: {
    alignSelf: "flex-start",
  },
  cardContent: {
    paddingHorizontal: 20,
  },
  courseName: {
    color: "#101828",
    lineHeight: 28,
  },
  courseCode: {
    color: "#64748B",
    fontSize: 13,
  },
  description: {
    color: "#64748B",
    lineHeight: 24,
  },
  statsContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#F8FAFC",
    paddingVertical: 16,
    paddingHorizontal: 20,
  },
  statItem: {
    alignItems: "center",
    flex: 1,
  },
  statNumber: {
    color: "#101828",
  },
  statLabel: {
    color: "#64748B",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginTop: 4,
  },
  footer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  updateIndicator: {
    flexDirection: "row",
    alignItems: "center",
  },
  updateDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 8,
  },
  updateText: {
    color: "#64748B",
    fontSize: 13,
  },
});

export default TeachingCard;
